import { useEffect, useState } from "react";
import { Menu, ArrowRight, CalendarClock, UserRound } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose,
} from "@/components/ui/sheet";
import { useSiteContent } from "@/context/ContentContext";

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const { header } = useSiteContent();
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Navigate to a homepage anchor, working from any route
  const go = (href) => {
    if (pathname !== "/") {
      navigate("/" + href);
    } else {
      scrollTo(href);
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = header.nav || [];

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-colors duration-300 ${
        scrolled
          ? "bg-[rgba(255,255,255,0.82)] backdrop-blur border-b border-black/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-5 sm:px-6 lg:px-8">
        <button
          data-testid="header-logo"
          onClick={() => {
            if (pathname !== "/") {
              navigate("/");
            } else {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          aria-label="Torna alla home"
          className="flex items-center gap-2.5 text-left"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-md border border-emerald-400/40 bg-emerald-600/10 font-display text-sm font-bold text-emerald-700">
            SX
          </span>
          <span className="font-display text-[15px] font-semibold tracking-tight text-[color:var(--text-primary)]">
            {header.brand}
          </span>
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {nav.map((n) => (
            <button
              key={n.href}
              data-testid={`nav-${n.href.replace("#", "")}`}
              onClick={() => go(n.href)}
              className="group relative text-sm text-secondary transition-colors hover:text-[color:var(--text-primary)]"
            >
              {n.label}
              <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-emerald-400 transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Link
            to="/blog"
            data-testid="header-blog-link"
            className="inline-flex h-10 items-center rounded-lg px-3 text-sm text-secondary transition-colors hover:text-[color:var(--text-primary)]"
          >
            Blog
          </Link>
          <Link
            to="/area-clienti"
            data-testid="header-client-area-link"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg px-3 text-sm text-secondary transition-colors hover:text-[color:var(--text-primary)]"
          >
            <UserRound className="h-4 w-4" />
            Area clienti
          </Link>
          <Link
            to="/prenota"
            data-testid="header-book-call-link"
            className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 text-sm font-medium text-emerald-700 transition-colors hover:bg-emerald-600/10"
          >
            <CalendarClock className="h-4 w-4" />
            Prenota una call
          </Link>
          <Button
            data-testid="header-primary-cta-button"
            onClick={() => go("#contatto")}
            className="h-10 rounded-lg bg-emerald-700 px-4 font-medium text-white transition-colors duration-200 hover:bg-emerald-600"
          >
            {header.cta}
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>

        {/* Mobile */}
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-trigger"
                aria-label="Apri menu"
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-black/10 text-[color:var(--text-primary)]"
              >
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="w-[82%] border-l border-black/10 bg-[var(--ink-900)] text-[color:var(--text-primary)]"
            >
              <div className="mt-8 flex flex-col gap-1">
                {nav.map((n) => (
                  <SheetClose asChild key={n.href}>
                    <button
                      data-testid={`mobile-nav-${n.href.replace("#", "")}`}
                      onClick={() => go(n.href)}
                      className="rounded-lg px-3 py-3 text-left text-base text-secondary hover:bg-black/[0.04] hover:text-[color:var(--text-primary)]"
                    >
                      {n.label}
                    </button>
                  </SheetClose>
                ))}
                <SheetClose asChild>
                  <Link
                    to="/blog"
                    data-testid="mobile-blog-link"
                    className="rounded-lg px-3 py-3 text-left text-base text-secondary hover:bg-black/[0.04] hover:text-[color:var(--text-primary)]"
                  >
                    Blog
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/prenota"
                    data-testid="mobile-book-call-link"
                    className="mt-4 flex items-center justify-center gap-1.5 rounded-lg border border-emerald-500/40 px-3 py-3 text-base font-medium text-emerald-700 hover:bg-emerald-600/10"
                  >
                    <CalendarClock className="h-4 w-4" />
                    Prenota una call
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link
                    to="/area-clienti"
                    data-testid="mobile-client-area-link"
                    className="flex items-center gap-2 rounded-lg px-3 py-3 text-left text-base text-secondary hover:bg-black/[0.04] hover:text-[color:var(--text-primary)]"
                  >
                    <UserRound className="h-4 w-4" />
                    Area clienti
                  </Link>
                </SheetClose>
                <SheetClose asChild>
                  <Button
                    data-testid="mobile-primary-cta-button"
                    onClick={() => go("#contatto")}
                    className="mt-2 h-11 rounded-lg bg-emerald-700 font-medium text-white hover:bg-emerald-600"
                  >
                    {header.cta}
                  </Button>
                </SheetClose>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
